'use client';

import { useMutation } from '@tanstack/react-query';
import { FieldArray, Form, Formik } from 'formik';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import EditorWYSIWYG from '../../components/EditorWYSIWYG';
import {
  CheckBox,
  SelectBoxWithSearch,
  TextArea,
  TextInput,
} from '../../components/Form/Form';
import BreadCrumb from '../../components/template/BreadCrumb';
import Header from '../../components/template/Header';
import api from '../../domain/api';
import {
  LINK_URL,
  PAGE_SINGLE_TITLE,
  PAGE_TITLE,
} from '../../shared/enums/activitys_enum';
import { useGetDropdownOptions } from '../../shared/hooks/UseActivity';
import { useSelectAllProcess } from '../../shared/hooks/UseProcess';
import { uploadGallery } from '../../shared/upload';

const DUE_TIME_FORMAT = (date = new Date()) => {
  // Format: yyyy-MM-ddTHH:mm (for datetime-local)
  const pad = (n) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const EnhancedAddActivity = () => {
  const history = useHistory();
  const [process_data] = useSelectAllProcess();
  const { all_processs } = process_data;

  // const [activity, addData] = useCreateActivity();
  const [featuredImage, setFeaturedImage] = useState(null);
  const [imagePicked, setImagePicked] = useState(null);
  const [attachmentPreviews, setAttachmentPreviews] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);

  console.log('featuredImage', featuredImage);
  console.log('imagePicked', imagePicked);

  const pickImage = ({ field, value }) => {
    const imagesCollection = imagePicked ? imagePicked : {};
    imagesCollection[field] = value;
    setImagePicked(imagesCollection);
    setFeaturedImage(imagesCollection);

    // Create previews for attachments
    if (field === 'attachments' && value) {
      const files = Array.from(value);
      const previews = files.map((file, index) => ({
        id: Date.now() + index,
        file: file,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: file.type.startsWith('image/')
          ? URL.createObjectURL(file)
          : null,
      }));
      setAttachmentPreviews(previews);
    }
  };

  const removeAttachment = (id) => {
    const updatedPreviews = attachmentPreviews.filter((item) => item.id !== id);
    setAttachmentPreviews(updatedPreviews);

    // Update the actual files
    const updatedFiles = updatedPreviews.map((item) => item.file);
    const fileList = new DataTransfer();
    updatedFiles.forEach((file) => fileList.items.add(file));

    pickImage({
      field: 'attachments',
      value: fileList.files,
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    const fileList = new DataTransfer();
    files.forEach((file) => fileList.items.add(file));

    pickImage({
      field: 'attachments',
      value: fileList.files,
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    );
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return 'fa-image';
    if (type.includes('pdf')) return 'fa-file-pdf';
    if (type.includes('word')) return 'fa-file-word';
    if (type.includes('excel') || type.includes('spreadsheet'))
      return 'fa-file-excel';
    if (type.includes('zip') || type.includes('rar')) return 'fa-file-archive';
    return 'fa-file';
  };

  const activityMutation = useMutation({
    mutationFn: async (data) => {
      const resp = await api.post(`/activitys`, data);
      return resp.data;
    },

    onSuccess() {
      toast.success('Activity created successfully!');
      history.push(`/${LINK_URL}`);
    },
    onError(error) {
      toast.error('Failed to create activity: ' + error.message);
    },
  });

  const submitFormClicked = async (values) => {
    const uploadedFiles = await uploadGallery(imagePicked?.attachments);

    activityMutation.mutate({
      ...values,
      attachments: uploadedFiles,
    });
  };

  const [dropdownOptions] = useGetDropdownOptions();

  return (
    <>
      <style jsx>{`
        .enhanced-activity-container {
          background: linear-gradient(
            135deg,
            #f8fafc 0%,
            #fef2f2 50%,
            #fff1f2 100%
          );
          min-height: 100vh;
          padding: 20px 0;
        }

        .enhanced-form-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid #f1f5f9;
          overflow: hidden;
          margin-bottom: 24px;
        }

        .enhanced-form-header {
          background: linear-gradient(
            135deg,
            #1e293b 0%,
            #334155 25%,
            #dc2626 75%,
            #b91c1c 100%
          );
          color: white;
          padding: 24px 28px;
          position: relative;
        }

        .enhanced-form-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            45deg,
            rgba(255, 255, 255, 0.1) 0%,
            transparent 50%
          );
          pointer-events: none;
        }

        .enhanced-form-title {
          font-size: 24px;
          font-weight: 700;
          margin: 0 0 8px 0;
          position: relative;
          z-index: 1;
        }

        .enhanced-form-subtitle {
          font-size: 14px;
          opacity: 0.9;
          margin: 0;
          position: relative;
          z-index: 1;
        }

        .enhanced-form-body {
          padding: 32px 28px;
        }

        .enhanced-form-section {
          margin-bottom: 32px;
        }

        .enhanced-section-title {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .enhanced-section-icon {
          width: 32px;
          height: 32px;
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 14px;
        }

        /* Enhanced Form Controls */
        .form-group {
          margin-bottom: 24px;
        }

        .form-group label {
          font-weight: 600;
          color: #374151;
          margin-bottom: 8px;
          display: block;
          font-size: 14px;
        }

        .form-control {
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          padding: 12px 16px;
          font-size: 14px;
          transition: all 0.2s ease;
          background: #fafafa;
        }

        .form-control:focus {
          outline: none;
          border-color: #dc2626;
          background: white;
          box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
        }

        /* Enhanced Attachment Section */
        .enhanced-attachment-section {
          background: #f9fafb;
          border: 2px dashed #d1d5db;
          border-radius: 12px;
          padding: 24px;
          text-align: center;
          transition: all 0.3s ease;
          cursor: pointer;
          position: relative;
        }

        .enhanced-attachment-section.drag-over {
          border-color: #dc2626;
          background: #fef2f2;
          transform: scale(1.02);
        }

        .enhanced-attachment-section:hover {
          border-color: #dc2626;
          background: #fef2f2;
        }

        .attachment-upload-icon {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          color: white;
          font-size: 24px;
        }

        .attachment-upload-title {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 8px;
        }

        .attachment-upload-subtitle {
          font-size: 14px;
          color: #6b7280;
          margin-bottom: 16px;
        }

        .attachment-upload-button {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          color: white;
          border: none;
          border-radius: 8px;
          padding: 10px 20px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .attachment-upload-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
        }

        .attachment-file-input {
          position: absolute;
          opacity: 0;
          width: 100%;
          height: 100%;
          cursor: pointer;
        }

        /* Attachment Previews */
        .attachment-previews {
          margin-top: 20px;
        }

        .attachment-preview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 16px;
          margin-top: 16px;
        }

        .attachment-preview-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 16px;
          transition: all 0.2s ease;
          position: relative;
        }

        .attachment-preview-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .attachment-preview-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
        }

        .attachment-file-icon {
          width: 40px;
          height: 40px;
          background: #f3f4f6;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6b7280;
          font-size: 16px;
        }

        .attachment-file-info {
          flex: 1;
          min-width: 0;
        }

        .attachment-file-name {
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 4px 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .attachment-file-size {
          font-size: 12px;
          color: #6b7280;
          margin: 0;
        }

        .attachment-remove-btn {
          position: absolute;
          top: 8px;
          right: 8px;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.2s ease;
        }

        .attachment-remove-btn:hover {
          background: #dc2626;
          transform: scale(1.1);
        }

        .attachment-image-preview {
          width: 100%;
          height: 120px;
          object-fit: cover;
          border-radius: 8px;
          margin-bottom: 12px;
        }

        /* Sidebar Cards */
        .enhanced-sidebar-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          border: 1px solid #f1f5f9;
          margin-bottom: 20px;
          overflow: hidden;
        }

        .enhanced-sidebar-header {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .enhanced-sidebar-title {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 4px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .enhanced-sidebar-subtitle {
          font-size: 12px;
          color: #6b7280;
          margin: 0;
        }

        .enhanced-sidebar-body {
          padding: 20px;
        }

        .checklist-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          margin-bottom: 8px;
          transition: all 0.2s ease;
        }

        .checklist-item:hover {
          background: #f3f4f6;
        }

        .checklist-item input[type='checkbox'] {
          width: 18px;
          height: 18px;
          accent-color: #dc2626;
        }

        .checklist-item label {
          font-size: 14px;
          color: #374151;
          margin: 0;
          cursor: pointer;
          flex: 1;
        }

        .sop-item {
          display: flex;
          gap: 12px;
          padding: 12px;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          margin-bottom: 12px;
        }

        .sop-icon {
          width: 32px;
          height: 32px;
          background: #dc2626;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 14px;
          flex-shrink: 0;
        }

        .sop-content {
          flex: 1;
          font-size: 14px;
          color: #374151;
          line-height: 1.5;
        }

        /* Enhanced Buttons */
        .enhanced-btn {
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.2s ease;
          border: none;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .enhanced-btn-primary {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
        }

        .enhanced-btn-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
        }

        .enhanced-btn-secondary {
          background: #f8f9fa;
          color: #6b7280;
          border: 1px solid #e5e7eb;
        }

        .enhanced-btn-secondary:hover {
          background: #e5e7eb;
          color: #374151;
        }

        .enhanced-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .enhanced-btn-loading {
          position: relative;
        }

        .enhanced-btn-loading::before {
          content: '';
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% {
            transform: translate(-50%, -50%) rotate(0deg);
          }
          100% {
            transform: translate(-50%, -50%) rotate(360deg);
          }
        }

        /* Form Actions */
        .enhanced-form-actions {
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
          padding: 20px 28px;
          display: flex;
          justify-content: center;
          gap: 16px;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .enhanced-form-body {
            padding: 20px;
          }

          .enhanced-form-header {
            padding: 20px;
          }

          .attachment-preview-grid {
            grid-template-columns: 1fr;
          }

          .enhanced-form-actions {
            padding: 16px 20px;
            flex-direction: column;
          }
        }

        /* Toggle Switch Styles */
        .toggle-switch-wrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 16px;
        }
        .toggle-switch {
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          user-select: none;
        }
        .toggle-switch input[type='checkbox'] {
          display: none;
        }
        .slider {
          width: 44px;
          height: 24px;
          background: #e5e7eb;
          border-radius: 24px;
          position: relative;
          transition: background 0.2s;
        }
        .slider:before {
          content: '';
          position: absolute;
          left: 4px;
          top: 4px;
          width: 16px;
          height: 16px;
          background: #fff;
          border-radius: 50%;
          transition: transform 0.2s;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
        }
        .toggle-switch input[type='checkbox']:checked + .slider {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
        }
        .toggle-switch input[type='checkbox']:checked + .slider:before {
          transform: translateX(20px);
        }
        .toggle-label {
          font-size: 14px;
          color: #374151;
          font-weight: 600;
        }

        .btn-xs.btn-today-quick,
        .btn-xs.btn-chip-quick {
          min-width: 0;
          height: 22px;
          line-height: 1;
          margin: 0;
        }
        .btn-chip-active {
          filter: brightness(1.1) contrast(1.1);
        }
      `}</style>

      <div className="pace-done">
        <div>
          <Header />
          <BreadCrumb
            title={`Add ${PAGE_SINGLE_TITLE}`}
            mainLinkTitle={PAGE_TITLE}
            mainLinkUrl={LINK_URL}
            activeLink="Add"
          />
        </div>

        <div className="enhanced-activity-container">
          <div className="container-fluid">
            <Formik
              initialValues={{
                task_name: '',
                assigned_to: '',
                activity_cycle: '',
                due_time: '',
                client: '',
                process: '',
                sop: '',
                checklist: [],
                estimated_time: '',
                actual_time: '',
                task_description: '',
                attachments: '',
                review_status: '',
                activity_status: '',
                comments: '',
              }}
              validationSchema={Yup.object({
                task_name: Yup.string().required('Required'),
                assigned_to: Yup.string().required('Required'),
                due_time: Yup.string().required('Required'),
                client: Yup.string().required('Required'),
                process: Yup.string().required('Required'),
                review_status: Yup.string().required('Required'),
                activity_status: Yup.string().required('Required'),
              })}
              onSubmit={async (values, { setSubmitting, resetForm }) => {
                const currentActivityStatus =
                  dropdownOptions.activity_status?.find(
                    (item) => item.value === values.activity_status
                  );

                console.log(currentActivityStatus, 'currentActivityStatus');

                if (
                  currentActivityStatus.label === 'COMPLETED' &&
                  Array.isArray(values.checklist) &&
                  values.checklist.some((item) => !item.is_checked)
                ) {
                  toast.error('Please check all the checklist items');
                  setSubmitting(false);
                  return;
                }

                // return;

                setSubmitting(true);
                submitFormClicked(values);
                // resetForm();
                setSubmitting(false);
              }}
            >
              {(formik) => {
                return (
                  <Form>
                    <div className="row">
                      <div className="col-md-8">
                        <div className="enhanced-form-card">
                          <div
                            className="enhanced-form-header"
                            style={{ color: 'white' }}
                          >
                            <h2
                              className="enhanced-form-title"
                              style={{ color: 'white' }}
                            >
                              <i className="fa fa-tasks me-2"></i>
                              Create New Activity
                            </h2>
                            <p className="enhanced-form-subtitle">
                              Fill in the details below to create a new activity
                              task
                            </p>
                          </div>

                          <div className="enhanced-form-body">
                            {/* Basic Information Section */}
                            <div className="enhanced-form-section">
                              <h3 className="enhanced-section-title">
                                <div className="enhanced-section-icon">
                                  <i className="fa fa-info"></i>
                                </div>
                                Basic Information
                              </h3>
                              <div className="row">
                                <div className="col-md-6">
                                  <TextInput
                                    label="Task Name"
                                    name="task_name"
                                    type="text"
                                    placeholder="Enter task name"
                                  />
                                </div>
                                <div className="col-md-6">
                                  <SelectBoxWithSearch
                                    label="Assigned to"
                                    name="assigned_to"
                                    options={
                                      dropdownOptions?.assigned_to?.map(
                                        (item) => ({
                                          value: item.value,
                                          label: item.label,
                                        })
                                      ) || []
                                    }
                                  />
                                </div>
                                <div className="col-md-6">
                                  <SelectBoxWithSearch
                                    label="Activity Cycle"
                                    name="activity_cycle"
                                    options={
                                      dropdownOptions?.activity_cycle?.map(
                                        (item) => ({
                                          value: item.value,
                                          label: item.label,
                                        })
                                      ) || []
                                    }
                                  />
                                </div>
                                <div className="col-md-6">
                                  <div
                                    style={{
                                      position: 'relative',
                                    }}
                                  >
                                    <TextInput
                                      label="Due Time"
                                      name="due_time"
                                      type="datetime-local"
                                      placeholder="Select due date and time"
                                    />
                                    <div
                                      style={{
                                        display: 'flex',

                                        justifyContent: 'flex-end',
                                      }}
                                    >
                                      <button
                                        type="button"
                                        className="btn btn-xs btn-today-quick"
                                        style={{
                                          background:
                                            'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: 6,
                                          fontSize: 12,
                                          padding: '2px 10px',
                                          fontWeight: 600,
                                          boxShadow:
                                            '0 1px 4px rgba(220,38,38,0.08)',
                                          textAlign: 'right',
                                        }}
                                        onClick={() => {
                                          formik.setFieldValue(
                                            'due_time',
                                            DUE_TIME_FORMAT()
                                          );
                                        }}
                                        title="Set to now"
                                      >
                                        Today
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <SelectBoxWithSearch
                                    label="Client"
                                    name="client"
                                    options={
                                      dropdownOptions?.client?.map((item) => ({
                                        value: item.value,
                                        label: item.label,
                                      })) || []
                                    }
                                  />
                                </div>
                                <div className="col-md-6">
                                  <SelectBoxWithSearch
                                    label="Process"
                                    name="process"
                                    placeholder="Select process"
                                    onChange={(e) => {
                                      formik.setFieldValue(
                                        'process',
                                        e.target.value
                                      );
                                      formik.setFieldValue('checklist', []);

                                      if (all_processs) {
                                        const filteredData =
                                          all_processs.filter(
                                            (item, index) =>
                                              item._id == e.target.value
                                          );

                                        if (
                                          filteredData &&
                                          filteredData.length > 0
                                        ) {
                                          const checklistData =
                                            filteredData[0].checklist;
                                          if (
                                            checklistData &&
                                            checklistData.length > 0
                                          ) {
                                            const checklistDataMapped =
                                              checklistData.map((item) => {
                                                return {
                                                  name: item.name,
                                                  is_checked: false,
                                                };
                                              });
                                            formik.setFieldValue(
                                              'checklist',
                                              checklistDataMapped
                                            );
                                          }
                                        }
                                      }
                                    }}
                                    options={dropdownOptions?.process?.map(
                                      (item) => ({
                                        value: item.value,
                                        label: item.label,
                                      })
                                    )}
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Time Tracking Section */}
                            <div className="enhanced-form-section">
                              <h3 className="enhanced-section-title">
                                <div className="enhanced-section-icon">
                                  <i className="fa fa-clock"></i>
                                </div>
                                Time Tracking
                              </h3>
                              <div className="row">
                                <div className="col-md-6">
                                  <label style={{ fontWeight: 600 }}>
                                    Estimated Time (minutes)
                                  </label>
                                  <div style={{ position: 'relative' }}>
                                    <TextInput
                                      name="estimated_time"
                                      type="number"
                                      placeholder="Enter estimated time"
                                    />
                                    <div
                                      style={{
                                        display: 'flex',
                                        gap: 6,
                                        marginTop: 4,
                                      }}
                                    >
                                      {[
                                        30, 45, 60, 120, 180, 240, 360, 480,
                                      ].map((val) => (
                                        <button
                                          key={val}
                                          type="button"
                                          className={`btn btn-xs btn-chip-quick ${
                                            formik.values.estimated_time == val
                                              ? 'btn-chip-active'
                                              : ''
                                          }`}
                                          style={{
                                            background:
                                              formik.values.estimated_time ==
                                              val
                                                ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                                                : '#f3f4f6',
                                            color:
                                              formik.values.estimated_time ==
                                              val
                                                ? 'white'
                                                : '#374151',
                                            border: 'none',
                                            borderRadius: 6,
                                            fontSize: 12,
                                            padding: '2px 10px',
                                            fontWeight: 600,
                                            boxShadow:
                                              formik.values.estimated_time ==
                                              val
                                                ? '0 1px 4px rgba(220,38,38,0.18)'
                                                : 'none',
                                          }}
                                          onClick={() =>
                                            formik.setFieldValue(
                                              'estimated_time',
                                              val
                                            )
                                          }
                                        >
                                          {val}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <label style={{ fontWeight: 600 }}>
                                    Actual Time (minutes)
                                  </label>
                                  <div style={{ position: 'relative' }}>
                                    <TextInput
                                      name="actual_time"
                                      type="number"
                                      placeholder="Enter actual time"
                                    />
                                    <div
                                      style={{
                                        display: 'flex',
                                        gap: 6,
                                        marginTop: 4,
                                      }}
                                    >
                                      {[
                                        30, 45, 60, 120, 180, 240, 360, 480,
                                      ].map((val) => (
                                        <button
                                          key={val}
                                          type="button"
                                          className={`btn btn-xs btn-chip-quick ${
                                            formik.values.actual_time == val
                                              ? 'btn-chip-active'
                                              : ''
                                          }`}
                                          style={{
                                            background:
                                              formik.values.actual_time == val
                                                ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
                                                : '#f3f4f6',
                                            color:
                                              formik.values.actual_time == val
                                                ? 'white'
                                                : '#374151',
                                            border: 'none',
                                            borderRadius: 6,
                                            fontSize: 12,
                                            padding: '2px 10px',
                                            fontWeight: 600,
                                            boxShadow:
                                              formik.values.actual_time == val
                                                ? '0 1px 4px rgba(16,185,129,0.18)'
                                                : 'none',
                                          }}
                                          onClick={() =>
                                            formik.setFieldValue(
                                              'actual_time',
                                              val
                                            )
                                          }
                                        >
                                          {val}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Description Section */}
                            <div className="enhanced-form-section">
                              <h3 className="enhanced-section-title">
                                <div className="enhanced-section-icon">
                                  <i className="fa fa-align-left"></i>
                                </div>
                                Task Description
                              </h3>
                              <div className="form-group">
                                <EditorWYSIWYG
                                  value={
                                    formik.values.task_description
                                      ? formik.values.task_description
                                      : ''
                                  }
                                  name={'task_description'}
                                  changeValue={(value) => {
                                    formik.setFieldValue(
                                      'task_description',
                                      value
                                    );
                                  }}
                                />
                                {formik.errors &&
                                  formik.errors['task_description'] && (
                                    <p className="text-danger">Required</p>
                                  )}
                              </div>
                            </div>

                            {/* Enhanced Attachments Section */}
                            <div className="enhanced-form-section">
                              <h3 className="enhanced-section-title">
                                <div className="enhanced-section-icon">
                                  <i className="fa fa-paperclip"></i>
                                </div>
                                Attachments
                              </h3>
                              <div
                                className={`enhanced-attachment-section ${
                                  isDragOver ? 'drag-over' : ''
                                }`}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onClick={() =>
                                  document
                                    .getElementById('attachment-input')
                                    .click()
                                }
                              >
                                <div className="attachment-upload-icon">
                                  <i className="fa fa-cloud-upload-alt"></i>
                                </div>
                                <h4 className="attachment-upload-title">
                                  {isDragOver
                                    ? 'Drop files here'
                                    : 'Upload Attachments'}
                                </h4>
                                <p className="attachment-upload-subtitle">
                                  Drag and drop files here, or click to browse
                                </p>
                                <button
                                  type="button"
                                  className="attachment-upload-button"
                                >
                                  <i className="fa fa-plus me-2"></i>
                                  Choose Files
                                </button>
                                <input
                                  id="attachment-input"
                                  type="file"
                                  multiple
                                  className="attachment-file-input"
                                  onChange={(e) =>
                                    pickImage({
                                      field: 'attachments',
                                      value: e.target.files,
                                    })
                                  }
                                />
                              </div>

                              {attachmentPreviews.length > 0 && (
                                <div className="attachment-previews">
                                  <h5
                                    style={{
                                      fontSize: '16px',
                                      fontWeight: '600',
                                      marginBottom: '16px',
                                    }}
                                  >
                                    Attached Files ({attachmentPreviews.length})
                                  </h5>
                                  <div className="attachment-preview-grid">
                                    {attachmentPreviews.map((attachment) => (
                                      <div
                                        key={attachment.id}
                                        className="attachment-preview-card"
                                      >
                                        <button
                                          type="button"
                                          className="attachment-remove-btn"
                                          onClick={() =>
                                            removeAttachment(attachment.id)
                                          }
                                          title="Remove file"
                                        >
                                          <i className="fa fa-times"></i>
                                        </button>

                                        {attachment.preview && (
                                          <img
                                            src={
                                              attachment.preview ||
                                              '/placeholder.svg'
                                            }
                                            alt={attachment.name}
                                            className="attachment-image-preview"
                                          />
                                        )}

                                        <div className="attachment-preview-header">
                                          <div className="attachment-file-icon">
                                            <i
                                              className={`fa ${getFileIcon(
                                                attachment.type
                                              )}`}
                                            ></i>
                                          </div>
                                          <div className="attachment-file-info">
                                            <h6
                                              className="attachment-file-name"
                                              title={attachment.name}
                                            >
                                              {attachment.name}
                                            </h6>
                                            <p className="attachment-file-size">
                                              {formatFileSize(attachment.size)}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Status Section */}
                            <div className="enhanced-form-section">
                              <h3 className="enhanced-section-title">
                                <div className="enhanced-section-icon">
                                  <i className="fa fa-flag"></i>
                                </div>
                                Status & Review
                              </h3>
                              <div className="row">
                                <div className="col-md-6">
                                  <label style={{ fontWeight: 600 }}>
                                    Review Status
                                  </label>
                                  <div style={{ position: 'relative' }}>
                                    <SelectBoxWithSearch
                                      name="review_status"
                                      placeholder="Select review status"
                                      options={
                                        dropdownOptions?.review_status?.map(
                                          (item) => ({
                                            value: item.value,
                                            label: item.label,
                                          })
                                        ) || []
                                      }
                                    />
                                    <div
                                      style={{
                                        display: 'flex',
                                        gap: 6,
                                        marginTop: 4,
                                      }}
                                    >
                                      {dropdownOptions?.review_status
                                        ?.filter((item) =>
                                          [
                                            'DONE',
                                            'ASSIGNED',
                                            'WORKING',
                                          ].includes(
                                            item.label.toUpperCase().trim()
                                          )
                                        )
                                        .map((item) => (
                                          <button
                                            key={item.value}
                                            type="button"
                                            className={`btn btn-xs btn-chip-quick ${
                                              formik.values.review_status ===
                                              item.value
                                                ? 'btn-chip-active'
                                                : ''
                                            }`}
                                            style={{
                                              background:
                                                formik.values.review_status ===
                                                item.value
                                                  ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                                                  : '#f3f4f6',
                                              color:
                                                formik.values.review_status ===
                                                item.value
                                                  ? 'white'
                                                  : '#374151',
                                              border: 'none',
                                              borderRadius: 6,
                                              fontSize: 12,
                                              padding: '2px 10px',
                                              fontWeight: 600,
                                              boxShadow:
                                                formik.values.review_status ===
                                                item.value
                                                  ? '0 1px 4px rgba(37,99,235,0.18)'
                                                  : 'none',
                                            }}
                                            onClick={() =>
                                              formik.setFieldValue(
                                                'review_status',
                                                item.value
                                              )
                                            }
                                          >
                                            {item.label}
                                          </button>
                                        ))}
                                    </div>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <label style={{ fontWeight: 600 }}>
                                    Activity Status
                                  </label>
                                  <div style={{ position: 'relative' }}>
                                    <SelectBoxWithSearch
                                      name="activity_status"
                                      placeholder="Select activity status"
                                      options={
                                        dropdownOptions?.activity_status?.map(
                                          (item) => ({
                                            value: item.value,
                                            label: item.label,
                                          })
                                        ) || []
                                      }
                                    />
                                    <div
                                      style={{
                                        display: 'flex',
                                        gap: 6,
                                        marginTop: 4,
                                      }}
                                    >
                                      {dropdownOptions?.activity_status
                                        ?.slice(0, 3)
                                        .map((item) => (
                                          <button
                                            key={item.value}
                                            type="button"
                                            className={`btn btn-xs btn-chip-quick ${
                                              formik.values.activity_status ===
                                              item.value
                                                ? 'btn-chip-active'
                                                : ''
                                            }`}
                                            style={{
                                              background:
                                                formik.values
                                                  .activity_status ===
                                                item.value
                                                  ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                                                  : '#f3f4f6',
                                              color:
                                                formik.values
                                                  .activity_status ===
                                                item.value
                                                  ? 'white'
                                                  : '#374151',
                                              border: 'none',
                                              borderRadius: 6,
                                              fontSize: 12,
                                              padding: '2px 10px',
                                              fontWeight: 600,
                                              boxShadow:
                                                formik.values
                                                  .activity_status ===
                                                item.value
                                                  ? '0 1px 4px rgba(220,38,38,0.18)'
                                                  : 'none',
                                            }}
                                            onClick={() =>
                                              formik.setFieldValue(
                                                'activity_status',
                                                item.value
                                              )
                                            }
                                          >
                                            {item.label}
                                          </button>
                                        ))}
                                    </div>
                                  </div>
                                </div>
                                <div className="col-md-12">
                                  <TextArea
                                    label="Comments"
                                    name="comment"
                                    type="text"
                                    placeholder="Enter any additional comments"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="enhanced-form-actions">
                            <button
                              type="submit"
                              className={`enhanced-btn enhanced-btn-primary ${
                                formik.isSubmitting
                                  ? 'enhanced-btn-loading'
                                  : ''
                              }`}
                              disabled={formik.isSubmitting}
                            >
                              {formik.isSubmitting ? (
                                <>
                                  <span style={{ opacity: 0 }}>
                                    <i className="fa fa-save me-2"></i>
                                    Save Activity
                                  </span>
                                </>
                              ) : (
                                <>
                                  <i className="fa fa-save me-2"></i>
                                  Save Activity
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              className="enhanced-btn enhanced-btn-secondary"
                              onClick={() => history.goBack()}
                            >
                              <i className="fa fa-arrow-left me-2"></i>
                              Go Back
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-4">
                        {/* Checklist Card */}
                        <div className="enhanced-sidebar-card">
                          <div className="enhanced-sidebar-header">
                            <h4 className="enhanced-sidebar-title">
                              <i className="fa fa-check-square"></i>
                              Process Checklist
                            </h4>
                            <p className="enhanced-sidebar-subtitle">
                              Complete all required steps
                            </p>
                          </div>
                          <div className="enhanced-sidebar-body">
                            {/* Toggle All Switch */}
                            {formik.values.checklist &&
                              formik.values.checklist.length > 0 && (
                                <div className="toggle-switch-wrapper">
                                  <label className="toggle-switch">
                                    <input
                                      type="checkbox"
                                      checked={formik.values.checklist.every(
                                        (item) => item.is_checked
                                      )}
                                      onChange={() => {
                                        const allChecked =
                                          formik.values.checklist.every(
                                            (item) => item.is_checked
                                          );
                                        const toggled =
                                          formik.values.checklist.map(
                                            (item) => ({
                                              ...item,
                                              is_checked: !allChecked,
                                            })
                                          );
                                        formik.setFieldValue(
                                          'checklist',
                                          toggled
                                        );
                                      }}
                                    />
                                    <span className="slider"></span>
                                    <span className="toggle-label">
                                      {formik.values.checklist.every(
                                        (item) => item.is_checked
                                      )
                                        ? 'Unselect All'
                                        : 'Select All'}
                                    </span>
                                  </label>
                                </div>
                              )}
                            <FieldArray
                              name="checklist"
                              render={(arrayHelper) => {
                                return (
                                  <div>
                                    {formik.values.checklist &&
                                    formik.values.checklist.length > 0 ? (
                                      formik.values.checklist.map(
                                        (item, index) => (
                                          <div
                                            key={index}
                                            className="checklist-item"
                                          >
                                            <CheckBox
                                              name={`checklist[${index}].is_checked`}
                                              type="checkbox"
                                            >
                                              {item.name}
                                            </CheckBox>
                                          </div>
                                        )
                                      )
                                    ) : (
                                      <div
                                        style={{
                                          textAlign: 'center',
                                          color: '#6b7280',
                                          padding: '20px',
                                        }}
                                      >
                                        <i
                                          className="fa fa-info-circle"
                                          style={{
                                            fontSize: '24px',
                                            marginBottom: '8px',
                                          }}
                                        ></i>
                                        <p>
                                          Select a process to view checklist
                                          items
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                );
                              }}
                            />
                          </div>
                        </div>

                        {/* SOP Card */}
                        <div className="enhanced-sidebar-card">
                          <div className="enhanced-sidebar-header">
                            <h4 className="enhanced-sidebar-title">
                              <i className="fa fa-book"></i>
                              Standard Operating Procedure
                            </h4>
                            <p className="enhanced-sidebar-subtitle">
                              Follow these guidelines
                            </p>
                          </div>
                          <div className="enhanced-sidebar-body">
                            {all_processs &&
                            all_processs.filter(
                              (item) => item._id == formik.values.process
                            ) &&
                            all_processs.filter(
                              (item) => item._id == formik.values.process
                            )[0] &&
                            all_processs.filter(
                              (item) => item._id == formik.values.process
                            )[0].sop ? (
                              all_processs
                                .filter(
                                  (item) => item._id == formik.values.process
                                )[0]
                                .sop.map((item, index) => (
                                  <div key={index} className="sop-item">
                                    <div className="sop-icon">
                                      <i className="fa fa-list-ol"></i>
                                    </div>
                                    <div
                                      className="sop-content"
                                      dangerouslySetInnerHTML={{
                                        __html: item.name,
                                      }}
                                    />
                                  </div>
                                ))
                            ) : (
                              <div
                                style={{
                                  textAlign: 'center',
                                  color: '#6b7280',
                                  padding: '20px',
                                }}
                              >
                                <i
                                  className="fa fa-info-circle"
                                  style={{
                                    fontSize: '24px',
                                    marginBottom: '8px',
                                  }}
                                ></i>
                                <p>Select a process to view SOP guidelines</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </div>
    </>
  );
};

export default EnhancedAddActivity;
